import React, { useState, useEffect, useRef } from "react";

interface ImageLazyTypes {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  radius?: "none" | "small" | "medium" | "large" | "full";
  defaultIsVisible?: boolean;
  style?: React.CSSProperties;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  srcSet?: string;
  sizes?: string;
}

const radiusMap = {
  none: "rounded-none",
  small: "rounded-sm",
  medium: "rounded-md",
  large: "rounded-lg",
  full: "rounded-full",
};

const placeholder = "https://assets.gazar.dev/placeholder_629c49f216.png";

function buildSrcSet(src: string, formats?: any): string | undefined {
  if (!formats) return undefined;
  const entries: string[] = [];
  if (formats.small?.url) entries.push(`${formats.small.url} 500w`);
  if (formats.medium?.url) entries.push(`${formats.medium.url} 750w`);
  if (formats.large?.url) entries.push(`${formats.large.url} 1000w`);
  if (formats.thumbnail?.url) entries.push(`${formats.thumbnail.url} 245w`);
  if (entries.length > 0) {
    entries.push(`${src} 1200w`);
    return entries.join(", ");
  }
  return undefined;
}

const ImageLazy: React.FC<ImageLazyTypes> = ({
  src,
  defaultIsVisible = false,
  alt,
  className = "",
  width,
  height,
  radius = "medium",
  style,
  objectFit = "cover",
  srcSet,
  sizes,
}: ImageLazyTypes) => {
  const newSrc = src
    ? /^https?:\/\//.test(src)
      ? src
      : src.startsWith("/")
        ? src
        : `https://assets.gazar.dev/${src?.split("/").pop()}`
    : placeholder;
  const [isVisible, setIsVisible] = useState<boolean>(defaultIsVisible);
  const [imgError, setImgError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (defaultIsVisible) {
      setIsVisible(true);
      return;
    }
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { root: null, rootMargin: "200px", threshold: 0.01 }
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line
  }, [defaultIsVisible]);

  const composedStyle: React.CSSProperties = {
    ...style,
    width: width ? width : undefined,
    height: height ? height : undefined,
  };

  const defaultSizes = sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden flex items-center justify-center ${width ? "" : "w-full"} ${height ? "" : "h-full"}`}
      style={composedStyle}
    >
      <img
        src={isVisible && !imgError ? newSrc : placeholder}
        alt={alt}
        className={`transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"} ${className} ${radiusMap[radius] || "rounded-md"}`}
        onLoad={() => setLoaded(true)}
        onError={() => setImgError(true)}
        width={width}
        height={height}
        srcSet={isVisible && !imgError ? srcSet : undefined}
        sizes={srcSet ? defaultSizes : undefined}
        style={{ objectFit: objectFit, width: width ? width : "100%", height: height ? height : "100%" }}
        loading="lazy"
        decoding="async"
      />
      {imgError && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5] text-[#2d5016] text-xs">
          Image not found
        </div>
      )}
    </div>
  );
};

export { buildSrcSet };
export default ImageLazy;
